"use client";

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  useRef,
} from "react";

import { useUserNotifications } from "@/lib/orchestrator/notifications/api";
import { debounce } from "lodash";

import { ApiResponse } from "@/types/api";
import {
  Notification,
  NotificationCountsByCategory,
  NotificationDigestGroup
} from "@/lib/orchestrator/notifications/types";

interface NotificationContextProps {
  notifications: Notification[];
  unreadCount: number;
  categoryBreakdown: NotificationCountsByCategory[];
  digestView: NotificationDigestGroup[];
  refreshNotifications: (acknowledged?: boolean, category?: number, limit?: number) => Promise<ApiResponse<Notification[]> | undefined>;
  acknowledgeNotification: (id: string) => Promise<void>;
  acknowledgeAllNotifications: (category?: number) => Promise<void>;
  refreshUnreadCount: (groupByCategory?: boolean) => Promise<void>;
  refreshDigestView: (limitPerCategory?: number) => Promise<void>;
  getNotificationById: (id: string) => Promise<Notification | undefined>;
  lastUpdated: number | null;
  loading: boolean;
}

const NotificationContext = createContext<NotificationContextProps | undefined>(
  undefined,
);

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const {
    getUserNotifications,
    acknowledgeNotification: apiAcknowledgeNotification,
    acknowledgeAllNotifications: apiAcknowledgeAllNotifications,
    getNotificationCount,
    getNotificationDigest,
    getNotificationById,
    isLoading
  } = useUserNotifications();

  const [localNotifications, setLocalNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [categoryBreakdown, setCategoryBreakdown] = useState<NotificationCountsByCategory[]>([]);
  const [digestView, setDigestView] = useState<NotificationDigestGroup[]>([]);
  const [lastUpdated, setLastUpdated] = useState<number | null>(null);

  // Add ref to track initialization and prevent multiple API calls
  const initializationCompleted = useRef(false);
  const eventSourceRef = useRef<EventSource | null>(null);

  // Fetch notifications with debounce to prevent excessive API calls
  const fetchNotifications = useCallback(
    debounce(async (acknowledged?: boolean, category?: number, limit?: number) => {
      console.log("📩 Fetching notifications...");

      try {
        const newNotifications = await getUserNotifications(acknowledged, category, limit);
        if (newNotifications) {
          setLocalNotifications(newNotifications);
          setLastUpdated(Date.now());
        }
        return newNotifications;
      } catch (error) {
        console.error("Error fetching notifications:", error);
      }
    }, 1000),
    [getUserNotifications],
  );

  // Fetch unread count, optionally grouped by category
  const fetchUnreadCount = useCallback(
    async (groupByCategory: boolean = true) => {
      try {
        const countData = await getNotificationCount(groupByCategory);

        if (countData) {
          if (Array.isArray(countData)) {
            setCategoryBreakdown(countData as NotificationCountsByCategory[]);
            // Calculate total from all categories
            const total = countData.reduce((sum, item) => sum + item.count, 0);
            setUnreadCount(total);
          } else {
            setUnreadCount((countData as any).count || 0);
          }
        }
      } catch (error) {
        console.error("Error fetching unread count:", error);
      }
    },
    [getNotificationCount],
  );

  // Fetch notifications grouped by category
  const fetchDigestView = useCallback(
    async (limitPerCategory: number = 5) => {
      try {
        const digest = await getNotificationDigest(limitPerCategory, false);
        if (digest) {
          setDigestView(digest);
        }
      } catch (error) {
        console.error("Error fetching digest view:", error);
      }
    },
    [getNotificationDigest],
  );

  // Initialize data on component mount - but only once
  useEffect(() => {
    // Only run initialization once
    if (initializationCompleted.current) return;

    console.log("🔄 Initializing notification data...");
    initializationCompleted.current = true;

    // Fetch notifications (only unacknowledged by default)
    fetchNotifications(false);

    // Fetch counts and digest view
    fetchUnreadCount(true);
    fetchDigestView();
  }, []); // Empty dependency array to run only once

  // Set up SSE (Server-Sent Events) for real-time notifications
  useEffect(() => {
    // Clean up any existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    const eventSource = new EventSource("/api/notifications/webhook");
    eventSourceRef.current = eventSource;

    console.log("📡 EventSource created for notifications");

    eventSource.onopen = () => {
      console.log("📡 SSE connection opened");
    };

    eventSource.onmessage = (event) => {
      console.log("📡 SSE message received:", event.data);
      try {
        const data = JSON.parse(event.data);
        if (data.event === "new_notification") {
          console.log("🔔 New notification received!");

          // Refresh all notification data
          fetchNotifications(false);
          fetchUnreadCount(true);
          fetchDigestView();
        }
      } catch (error) {
        console.error("❌ Error parsing SSE event:", error);
      }
    };

    eventSource.onerror = (error) => {
      console.warn("❌ SSE connection error:", error);
      // Browser will attempt to reconnect automatically
    };

    return () => {
      console.log("Closing EventSource");
      eventSource.close();
      eventSourceRef.current = null;
    };
  }, []); // Empty dependency array to set up only once

  // Handle acknowledging a single notification
  const handleAcknowledgeNotification = async (id: string) => {
    try {
      await apiAcknowledgeNotification(id);

      // Update local state
      setLocalNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, acknowledged: true } : n))
      );

      // Refresh counts after acknowledgement
      fetchUnreadCount(true);

      setLastUpdated(Date.now());
    } catch (error) {
      console.error("Error acknowledging notification:", error);
      throw error;
    }
  };

  // Handle acknowledging all notifications
  const handleAcknowledgeAllNotifications = async (category?: number) => {
    try {
      await apiAcknowledgeAllNotifications(category);

      // Update local state
      if (category) {
        // If category is specified, only mark those from that category
        setLocalNotifications((prev) =>
          prev.map((n) => {
            if (n.category === String(category) || n.metadata?.category_id === category) {
              return { ...n, acknowledged: true };
            }
            return n;
          })
        );
      } else {
        // Mark all as acknowledged
        setLocalNotifications((prev) =>
          prev.map((n) => ({ ...n, acknowledged: true }))
        );
      }

      // Refresh counts after acknowledgement
      fetchUnreadCount(true);

      setLastUpdated(Date.now());
    } catch (error) {
      console.error("Error acknowledging all notifications:", error);
      throw error;
    }
  };

  return (
    <NotificationContext.Provider
      value={{
        notifications: localNotifications,
        unreadCount,
        categoryBreakdown,
        digestView,
        refreshNotifications: fetchNotifications,
        acknowledgeNotification: handleAcknowledgeNotification,
        acknowledgeAllNotifications: handleAcknowledgeAllNotifications,
        refreshUnreadCount: fetchUnreadCount,
        refreshDigestView: fetchDigestView,
        getNotificationById,
        lastUpdated,
        loading: isLoading
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotifications must be used within a NotificationProvider"
    );
  }
  return context;
};

// Type export for convenience
export type { Notification } from "@/lib/orchestrator/notifications/types";